/**
 * Decision-support maths for the Off-Grid Calculator: cost, payback, roof
 * space, battery autonomy and the live "cart" total.
 *
 * The backend (enviz_api routers/off_grid_calculator.py) owns the sizing
 * itself. Everything here is derived from what it returns, and the formulas
 * are deliberately the same ones, so a client-side recompute (a moved
 * autonomy slider, a ticked machine) lands on the number the backend would
 * give for the same inputs.
 */

/** Rule-of-thumb roof footprint of 1 kWp of panels, including walkways. */
export const SQM_PER_KWP = 7;
export const SQFT_PER_SQM = 10.7639;
export const SQFT_PER_KWP = SQM_PER_KWP * SQFT_PER_SQM;

/**
 * The backend sizes the battery for the worst night between 18:00 and 06:00.
 * Autonomy hours are scaled against that window.
 */
export const NIGHT_WINDOW_HOURS = 12;
export const DEFAULT_AUTONOMY_HOURS = NIGHT_WINDOW_HOURS;
export const MIN_AUTONOMY_HOURS = 1;
export const MAX_AUTONOMY_HOURS = 24;

/** Paybacks beyond this are shown as "N+" -- no panel or battery warranty covers more. */
export const PAYBACK_CAP_YEARS = 25;

/**
 * Placeholder market rates in PKR. The solar figure is the midpoint of the
 * Rs. 34,000-43,500 per kWp quoted for A-grade panels in Pakistan -- panels
 * only, so mounting, cabling and labour are NOT in it. They exist so the card shows
 * something sensible on first open; they are not quotes. The UI says so until
 * the user saves their own.
 */
export const DEFAULT_RATES = Object.freeze({
  solarPerKwp: 39000,
  batteryPerKwh: 55000,
  inverterPerKw: 25000,
  gridRate: "", // blank = use the unit price from the account
});

const RATE_KEYS = ["solarPerKwp", "batteryPerKwh", "inverterPerKw"];
const storageKey = (username) => `enviz_offgrid_rates_${username || "anon"}`;

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const positiveOrNull = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Saved rates for this user, merged over the defaults. Bad or missing data -> defaults. */
export function loadRates(username) {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey(username)) || "null");
    if (!saved || typeof saved !== "object") return { ...DEFAULT_RATES };
    const rates = { ...DEFAULT_RATES };
    RATE_KEYS.forEach((key) => {
      const v = positiveOrNull(saved[key]);
      if (v != null) rates[key] = v;
    });
    const grid = positiveOrNull(saved.gridRate);
    rates.gridRate = grid != null ? grid : "";
    return rates;
  } catch {
    return { ...DEFAULT_RATES };
  }
}

export function saveRates(username, rates) {
  try {
    localStorage.setItem(storageKey(username), JSON.stringify(rates));
  } catch {
    // Storage full or blocked: the session still works, it just won't persist.
  }
}

export function clearRates(username) {
  try {
    localStorage.removeItem(storageKey(username));
  } catch {
    // see saveRates
  }
}

/** True once the user has changed any rate from the placeholder. */
export function ratesAreCustomised(rates) {
  if (!rates) return false;
  return (
    RATE_KEYS.some((key) => num(rates[key]) !== DEFAULT_RATES[key]) ||
    positiveOrNull(rates.gridRate) != null
  );
}

export function clampAutonomyHours(hours) {
  const n = Math.round(num(hours));
  if (n < MIN_AUTONOMY_HOURS) return MIN_AUTONOMY_HOURS;
  if (n > MAX_AUTONOMY_HOURS) return MAX_AUTONOMY_HOURS;
  return n;
}

/**
 * Battery (Wh) to carry the worst night's *average* draw for `hours`.
 *
 * At the default 12 h this is exactly the backend's maxNighttimeLoadWh / DoD.
 * Beyond 12 h it assumes the worst night's average keeps going, which is the
 * only thing the data supports -- the UI says so.
 */
export function batteryWhForAutonomy(maxNighttimeLoadWh, hours, batteryDod = 0.9) {
  const dod = num(batteryDod) > 0 ? num(batteryDod) : 0.9;
  const averageNightW = num(maxNighttimeLoadWh) / NIGHT_WINDOW_HOURS;
  return (averageNightW * clampAutonomyHours(hours)) / dod;
}

/**
 * The backend's recommended system with only the battery re-derived for the
 * chosen autonomy. Solar and inverter are taken as returned.
 */
export function adjustSystem(solarSystem, loadAnalysis, constants, hours) {
  const solarWp = num(solarSystem?.solarWp);
  const inverterW = num(solarSystem?.inverterW);
  const hasNightLoad = loadAnalysis?.maxNighttimeLoadWh != null;
  const batteryWh = hasNightLoad
    ? batteryWhForAutonomy(loadAnalysis.maxNighttimeLoadWh, hours, constants?.batteryDod)
    : num(solarSystem?.batteryWh);
  return { solarWp, batteryWh, inverterW };
}

export function solarAreaSqFt(solarWp) {
  return (num(solarWp) / 1000) * SQFT_PER_KWP;
}

/** Itemised cost at the given rates (PKR per kWp / kWh / kW). Only as complete as the rates are. */
export function estimateCapex(system, rates) {
  const solar = (num(system?.solarWp) / 1000) * num(rates?.solarPerKwp);
  const battery = (num(system?.batteryWh) / 1000) * num(rates?.batteryPerKwh);
  const inverter = (num(system?.inverterW) / 1000) * num(rates?.inverterPerKw);
  return { solar, battery, inverter, total: solar + battery + inverter };
}

/**
 * Grid bill the off-grid system would avoid in a year (PKR).
 * `annualEnergyWh` is the backend's annualised figure, not the worst day.
 */
export function annualGridCost(annualEnergyWh, gridRate) {
  return (num(annualEnergyWh) / 1000) * num(gridRate);
}

/**
 * Simple payback in years, or null when it cannot be stated honestly (no
 * saving, no cost, or the saving is not known).
 */
export function paybackYears(capex, annualSavings) {
  if (!(num(capex) > 0) || !(num(annualSavings) > 0)) return null;
  return num(capex) / num(annualSavings);
}

/** Grid rate to price the saving at: the user's override, else the account's unit price. */
export function effectiveGridRate(rates, accountGridRate) {
  return positiveOrNull(rates?.gridRate) ?? positiveOrNull(accountGridRate) ?? 0;
}

/**
 * System sized from raw load figures, using the same formulas the backend does.
 * `constants` comes from the analyze response (systemEfficiency, pshUsed,
 * batteryDod, safetyMargin).
 */
export function buildSystem({ dailyWh, nightWh, peakW }, constants, hours = DEFAULT_AUTONOMY_HOURS) {
  const psh = num(constants?.pshUsed);
  const efficiency = num(constants?.systemEfficiency) || 0.75;
  const margin = num(constants?.safetyMargin) || 1.25;
  return {
    solarWp: psh > 0 ? num(dailyWh) / (psh * efficiency) : 0,
    batteryWh: batteryWhForAutonomy(nightWh, hours, constants?.batteryDod),
    inverterW: num(peakW) * margin,
  };
}

/**
 * Totals for a set of ticked machines, from the per-machine analysis already
 * fetched. Sizing is additive across machines on the backend (combined = sum),
 * so this needs no further request.
 *
 * `loads` is a Map of machine name -> loadAnalysis. Machines whose load has not
 * arrived are counted in `selected` but not in `known`, so the bar can say
 * "3 selected (2 with data)" rather than quietly under-reporting.
 */
export function summariseSelection(names, loads) {
  const totals = { selected: 0, known: 0, dailyWh: 0, nightWh: 0, peakW: 0, annualWh: 0 };
  names.forEach((name) => {
    totals.selected += 1;
    const load = loads?.get?.(name);
    if (!load) return;
    totals.known += 1;
    totals.dailyWh += num(load.totalDailyEnergyWh);
    totals.nightWh += num(load.maxNighttimeLoadWh);
    totals.peakW += num(load.peakInstantaneousLoadW);
    totals.annualWh += num(load.annualEnergyWh);
  });
  return totals;
}

/** "PKR 18.2M" for millions, grouped digits below that. Full value is for tooltips. */
export function formatPkr(value) {
  const n = num(value);
  const abs = Math.abs(n);
  if (abs >= 1e6) {
    return `PKR ${(n / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })}M`;
  }
  return `PKR ${Math.round(n).toLocaleString()}`;
}

export function formatPaybackYears(years) {
  if (years == null) return null;
  if (years > PAYBACK_CAP_YEARS) return `${PAYBACK_CAP_YEARS}+ years`;
  return `${years.toLocaleString(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 1 })} years`;
}
